import { useCallback, useEffect, useRef, useState } from 'react';
import {
    Check, ChevronDown, ChevronRight, ChevronsDown, CloudDownload, FileText, Inbox,
    Loader2, AlertTriangle, Upload,
} from 'lucide-react';
import { focusScopeLabel, type RailFocus } from '@/lib/railFocus';
import {
    listScans, getUnfiledDocuments, getDocumentUrl, deleteScan, getScanPreviewUrl,
    uploadDocument, voidDocumentApi, getPrintContent, ApiCallError,
    type ScanFileDto, type PreviewTarget,
} from '@/api/client';
import { useZebraPrint } from '@/lib/useZebraPrint';
import { printDocumentToDialog } from '@/lib/printDocument';
import { SignDocumentDialog } from '@/components/SignDocumentDialog';
import { AttachedPaperworkPanel, type AttachedTarget } from '@/components/AttachedPaperworkPanel';
import { ScanSourceToggle } from '@/components/ScanSourceToggle';
import { TrayRefreshButton } from '@/components/TrayRefreshButton';
import { TrayCard } from '@/components/PaperworkTrayCard';
import { DocumentThumb, isImageFile } from '@/components/DocumentThumb';
import type { DocumentDto } from '@/api/types';
import {
    snapshotFiles,
    isFileDrop as isFileDropDt, type PaperworkDragItem,
} from '@/lib/dnd';
import { prettyBytes } from '@/lib/scanFolder';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { DocumentPreview } from '@/components/DocumentPreview';
import { SECTION_HEADER } from '@/lib/utils';
import { useFileUpload } from '@/lib/useFileUpload';
import { useRailSlotClose } from '@/lib/railSlot';

/**
 * THE SCANNED PAPERWORK TRAY — scans waiting to be put on something (#150).
 *
 * ---- ⭐ THE NAME IS "SCANNED PAPERWORK", AND THE WORD EARNS ITS PLACE -------
 *
 * Pete, 2026-08-25: *"Lets change the right-column name from Paperwork to Scanned
 * Paperwork so there is a subtle difference between Paperwork on an appointment and
 * Scanned Paperwork not associated to anything yet."*
 *
 * ⭐ THE DISTINCTION IS THE WHOLE FEATURE. What is in this tray belongs to NOBODY yet —
 * it came off a scanner and is waiting for a person to decide which load it is for.
 * What sits under "Paperwork" on an appointment has already been decided. Two piles
 * called the same thing invites "I already filed that" about a document still sitting
 * here unclaimed.
 *
 * ⚠️ ScanFolderPanel CARRIES THE SAME HEADING, deliberately. It is the local-folder
 * equivalent of this tray and exactly one of the two is ever on screen; a scheduler
 * moving between a SharePoint site and a folder site must not think they are looking
 * at different features.
 *
 * ---- ⭐ IT LIVES IN THE ARRIVALS COLUMN, AND PETE IS THE REASON ------------
 *
 * It shipped as a strip across the BOTTOM of the schedule. Pete, 2026-08-25, after
 * one look at it: *"I don't like the section at the bottom. I want the right side
 * column to show the documents in order of scanned and do all the association from
 * there... When you have the items on the bottom, you start to lose view of the dock
 * doors and all the sites have a lot of dock doors."*
 *
 * ⛔ HE IS RIGHT AND THE ORIGINAL REASONING WAS WRONG. I put it at the bottom so it
 * would be permanently visible without stealing width. But the grid is TALL — Brighton
 * alone has 23 doors — so vertical space is the scarce thing, and a full-width strip
 * spends the scarcest resource to display a list that is usually short.
 *
 * ⭐ AND THE RIGHT COLUMN IS WHERE THE WORK ALREADY HAPPENS. The driver cards a
 * document gets dropped onto are in this same column, inches away. A drag that used to
 * cross the whole screen is now a short one, and nothing has to be dragged past the
 * grid it might be dropped on by accident.
 *
 * ⚠️ THE FILE NAME IS NOW WRONG AND IS KEPT ANYWAY. "Sliver" described the bottom
 * strip. Renaming it would orphan the old file — the desktop bridge writes but cannot
 * delete — and would break the markers in deploy-frontend-paperwork.ps1 and
 * paperwork-drag-guard.cjs for no behavioural gain. Tracked separately; the export
 * name is load-bearing until then.
 *
 * ---- ⚠️ TWO KINDS OF THING LIVE HERE, AND THEY ARE DIFFERENT ---------------
 *
 *   a SCAN     — still in SharePoint, not in Dock at all. Dropping it ingests it,
 *                then files it, then moves the original into Filed/.
 *   a DOCUMENT — already in Dock with no home. Dropping it just files it.
 *
 * Shown together because to the scheduler they are one pile, told apart by their icon
 * because the difference matters the moment something goes wrong.
 *
 * ⛔ NOTHING HERE FILES ANYTHING BY ITSELF (design §3). This renders the pile and
 * starts the drag; where a document lands is a person's decision.
 *
 * ⛔ AND IT RENDERS NOTHING AT ALL ON A LOCAL-FOLDER SITE. Those read their scans
 * through the browser's directory picker in ScanFolderPanel, and exactly one of the two
 * is ever on screen — migration 162's rule, carried up to the UI.
 */

interface Props {
    facilityCode: string;
    getToken: () => Promise<string>;
    /** Bumped by the page after a drop lands, so the pile reflects it immediately. */
    refreshKey?: number;
    /**
     * ⭐ Whether the page is mid-drop. The list greys out rather than disappearing:
     * a pile that vanishes under the cursor is how a scheduler ends up dropping the
     * next document on the wrong thing.
     */
    busy?: boolean;
    /**
     * ⭐⭐ THE LOAD THE CURSOR IS DWELLING ON, or null for the ordinary tray.
     * ⛔ SET FROM THE HOVER CARD'S OWN TIMER, never from mouseEnter — see
     * `onHoverCardShown` in GanttCalendar for why the distinction matters.
     */
    attached?: AttachedTarget | null;
    /**
     * ---- ⭐⭐⭐ THE RAIL'S SCOPE, SO THIS HEADER SPEAKS THE SAME SENTENCE --------
     *
     * > Pete, 2026-09-20: *"Use the same format as other panels Paperwork (all) or
     * > Paperwork (traffix)."*
     *
     * ⛔⛔ BOTH TRAY COMPONENTS TAKE IT, AND THAT IS NOT DUPLICATION FOR ITS OWN
     * SAKE. Exactly one of `ScanFolderPanel` and `PaperworkSliver` renders at a
     * site — SharePoint or local folder — so a header change built into one of them
     * ships green and is invisible at the other half of the estate. That has already
     * happened once (§4.14.1) and the `attached` prop above carries the same warning.
     *
     * ⚠️ NULL IS A VALUE HERE, NOT AN ABSENCE. `focusScopeLabel(null)` is `(ALL)`,
     * which is the correct and deliberate reading of an unscoped board — not a
     * missing label to be hidden.
     */
    focus?: RailFocus | null;
    /** ⭐ Which pile is on screen — see the §1R.16 note in ScanFolderPanel. */
    sourceView: 'SharePoint' | 'LocalFolder';
    /** ⭐ Which of the three the tray is showing — see Calendar's `trayView`. */
    trayView: 'SharePoint' | 'LocalFolder' | 'Attached';
    onTrayView: (v: 'SharePoint' | 'LocalFolder' | 'Attached') => void;
    /** ⭐ A scan dropped on the Attached icon — files onto the load it shows. */
    onDropAttached?: (e: React.DragEvent) => void;
    /** ⭐ A filed document dropped on the pile icon — moves it back. */
    onDropUnfile?: (e: React.DragEvent) => void;
    /** ⛔ Dropped on "This machine", which can only ever explain why it cannot. */
    onDropLocal?: (e: React.DragEvent) => void;
}

/** ⭐ How long a one-off confirmation stays on a rail that is short of room. */
const BANNER_MS = 6_000;

const POLL_MS = 15_000;

export function PaperworkSliver({
    facilityCode, getToken, refreshKey, busy, attached,
    focus = null,
    sourceView, trayView, onTrayView, onDropAttached, onDropUnfile, onDropLocal,
}: Props) {
    /**
     * ⛔⛔ COLLAPSED BY DEFAULT (2026-08-27). Pete: *"Lets make the Sliders all collapsed
     * by default with that arrow on the left side except driver checkins. So the user has
     * to expand the arrow to see other items."*
     *
     * ⚠️ THE RAIL HAS BEEN LOSING A FIGHT FOR HEIGHT ALL DAY — the phone line and the
     * Needs-license checkbox (#310) were both moved to claw back single lines. Three
     * sections open at once is the real cause: the check-ins, which are the reason the
     * rail exists, get whatever is left over.
     *
     * ⭐ SO THE DEFAULT IS NOW "one row of chrome", and each section carries its counts
     * IN ITS HEADER — which is why folding them costs nothing: a folded Communications
     * still says how many failed, and a folded Labor still says how many are in progress.
     * ⛔ Sections whose headers were silent could not have been folded this way.
     *
     * ⚠️ DRIVER CHECK-INS ARE NOT ONE OF THESE and stay expanded. They are the panel.
     */
    const [open, setOpen] = useState(false);

    /* ⭐ TELL THE RAIL — the call moved DOWN to sit beside `source`, because the
       report is now conditional on it. See `lib/railSlot.ts`. */

    /**
     * ---- ⭐⭐ SWITCHING THE SOURCE OPENS THE TRAY (2026-09-10) ---------------
     *
     * > Pete: *"Issue when go to this machine, now stuck"* — the other tray took
     * > over, collapsed, and looked broken.
     *
     * ⛔ THE TWO TRAYS DO NOT SHARE `open`, and they cannot: they are siblings
     * with their own state and the rail folds them independently. So switching
     * source swapped a panel the scheduler had opened for one they had not, and
     * the answer to "where did my scans go" was "behind a chevron".
     *
     * ⚠️ ON A CHANGE, NOT ON MOUNT. The ref starts holding the first value, so
     * the first render never fires this — #318's "starts collapsed" survives,
     * and only an actual switch overrides it.
     */
    /**
     * ⭐⭐ POINTING AT A LOAD OPENS THE TRAY. This is what "hover does nothing"
     * actually needed: the target arrives, so the section that shows it unfolds.
     * ⛔ Not `attached` — `trayView`. The target is remembered after you leave the
     * attached view (that is what keeps the third icon live), so keying on it would
     * re-open this section every time anything re-rendered.
     */
    useEffect(() => {
        if (trayView === 'Attached') setOpen(true);
    }, [trayView, attached]);

    const lastSourceView = useRef(sourceView);
    useEffect(() => {
        if (lastSourceView.current === sourceView) return;
        lastSourceView.current = sourceView;
        if (sourceView === 'SharePoint') setOpen(true);
    }, [sourceView]);

    const [scans, setScans] = useState<ScanFileDto[]>([]);
    const [unfiled, setUnfiled] = useState<DocumentDto[]>([]);
    const [source, setSource] = useState<string | null>(null);

    /* ⛔⛔ THE THIRD ARGUMENT IS THE SCROLLBAR FIX. This component RETURNS NULL
       on a local-folder view (see the early return far below) and used to keep
       reporting the PAPERWORK slot anyway — so a tray drawing nothing could take
       the slot's `flex-1` away from the tray that was. `lib/railSlot.ts` carries
       the measurement. ⚠️ The condition must MATCH those early returns exactly;
       `paperwork-tray-guard` asserts both — INCLUDING `source === null`, the
       config-not-loaded window, which also draws nothing. */
    /**
     * ⛔⛔ ~~THE ACCORDION~~ REVERTED 2026-09-10, SAME DAY, BY PETE: *"now they
     * don't work. none of them expand. Put it back."*
     *
     * ⚠️ THE FAULT WAS A RENDER/COMMIT RACE, not the idea. Clicking a chevron set
     * `open = true`; on THAT render the rail had not been told yet, so `openCodes`
     * was still empty and the yield signal read TRUE for the panel that had just
     * opened. Both effects then ran in the same commit — the report went up and the
     * panel closed itself. It closed on the way open, every time.
     *
     * ⛔⛔ AND THE SIMULATION THAT PASSED IT TESTED THE WRONG THING. It modelled
     * report → reduce → read as a settle-to-fixpoint loop and converged in two
     * passes. React captures the yield value during RENDER, before the report has
     * updated the context — the simulation had no render/commit ordering in it at
     * all, so it could not have caught this. §4.14, in the check written to prove
     * the accordion safe.
     *
     * ⭐ WHEN IT COMES BACK: report SYNCHRONOUSLY from the click, alongside
     * `setOpen`, so the next render already has the new `openCodes` and the stale
     * window does not exist. An effect is the wrong place for it.
     */
    /* ⭐ DECISION 193 — the two-panel cap. `useRailSlotClose` reports this panel's
       state upward AND closes it when the rail says a third panel pushed it out.
       ⛔ The policy is `nextRailSlots` in lib/railSlot.ts, proven by
       tools/rail-slot-test.cjs — not reasoned about here. */
    useRailSlotClose('PAPERWORK', open, setOpen, sourceView !== 'LocalFolder' && source !== null);

    const [reason, setReason] = useState<string | null>(null);
    const [truncated, setTruncated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    /** ⛔ The scan awaiting a DELETE confirmation. Never window.confirm. */
    const [confirmDelete, setConfirmDelete] = useState<ScanFileDto | null>(null);
    const [deleting, setDeleting] = useState(false);
    /**
     * ⭐ THE DOCUMENT AWAITING REMOVAL — A DIFFERENT ACT FROM BINNING A SCAN.
     *
     * Pete, 2026-08-25, looking at a hotel folio he had dropped in to test the upload:
     * *"Not showing me a delete button."* He was right, and it was an omission rather
     * than a decision — only SharePoint scans had one.
     *
     * ⛔ AND IT IS A VOID, NOT A DELETE. A scan lives in SharePoint and Dock has never
     * seen it, so binning it costs nothing. A document is already IN Dock: the bytes
     * are stored and there is a row recording that they arrived. "We had this and it
     * was removed on this date" and "we never had this" are completely different
     * sentences in a dispute, and deleting the row would make them indistinguishable.
     */
    const [confirmVoid, setConfirmVoid] = useState<DocumentDto | null>(null);
    const [voiding, setVoiding] = useState(false);
    /**
     * ⭐ WHAT THE PANEL BESIDE THE TRAY IS SHOWING. Pete, 2026-08-25: *"can we do the
     * document viewer as a popup next to the paperwork instead of a new tab?"*
     *
     * ⚠️ THE NAME IS CARRIED ALONGSIDE THE TARGET, not looked up when it is needed.
     * The 15-second poll can drop the row out from under an open preview — a scan that
     * somebody else just filed — and a header that goes blank while you are reading
     * the document is worse than one showing the name it opened with.
     */
    const [viewing, setViewing] = useState<{ target: PreviewTarget; name: string } | null>(null);

    /**
     * ---- ⭐ PRINTING A LABEL FROM THE TRAY (§1AK) --------------------------
     *
     * > Pete, 2026-09-09: *"The tray button on documents in Scanned Paperwork?
     * > Has to be a label printer type icon"*
     *
     * ⛔⛔ THIS PANEL IS THE TRAY, NOT `ScanFolderPanel`. The button went into
     * that file first and never appeared on screen: `Calendar.tsx` renders BOTH,
     * and THIS is the one that draws the rail card headed "Scanned Paperwork".
     * ⚠️ Every guard written for it passed, because every guard asserted about
     * the file that had been edited rather than the one that renders.
     *
     * ⛔ THE OUTCOME IS ALWAYS SHOWN. `printLabel` never throws and always
     * returns a sentence — a print that quietly does nothing is the worst
     * outcome on a dock, and an empty catch here would throw that away.
     */
    const zebra = useZebraPrint({ getToken, facilityCode });
    const [printed, setPrinted] = useState<string | null>(null);

    /** ⭐ The row being stamped as received. Null when the dialog is closed. */
    const [signing, setSigning] = useState<DocumentDto | null>(null);

    /**
     * ---- ⭐ PRINT A NON-LABEL DOCUMENT (Pete, 2026-09-10) ------------------
     *
     * > *"We need a printer icon and Sign Paperwork on the Scanned Paperwork
     * > panel for non-zpl docs."*
     *
     * ⛔ A DIFFERENT ICON AND A DIFFERENT PATH FROM THE BARCODE, deliberately.
     * The barcode sends raw ZPL to a Zebra with no dialog; this opens the OS
     * print dialog for a scan. ⚠️ Sending a PDF down the Zebra path would feed
     * label stock until somebody stopped it — the two must never be one button.
     */
    async function printPaper(d: DocumentDto) {
        setError(null);
        setPrinted(null);
        const outcome = await printDocumentToDialog(
            getToken, { kind: 'document', documentId: d.documentId }, d.fileName);
        setPrinted(outcome.message);
    }
    /** The tray element, so the panel can be placed beside it rather than at a corner. */
    const trayRef = useRef<HTMLDivElement>(null);
    /** ⭐ True while a file from Explorer is hovering over the tray. */
    const [dropping, setDropping] = useState(false);
    /**
     * ⭐⭐ THE UPLOAD LOOP IS SHARED SINCE 2026-09-08 (`lib/useFileUpload.ts`).
     * `upl.busy` and `upl.added` replace the `uploading` / `uploaded` state this
     * panel used to hold.
     *
     * ⛔ A COUNT, NOT A BOOLEAN — reason unchanged, and it now lives with the
     * loop: `true` would say "it worked"; the number says WHAT worked, and
     * "1 file added" after dropping two is a fault a boolean cannot show.
     *
     * ⛔⛔ `onError` FEEDS THIS PANEL'S OWN `error`, DELIBERATELY. There are TWO
     * error banners here — one when the panel is collapsed, one inside it when
     * open — and both read `error`. ⚠️ Rendering the hook's own `error` instead
     * would add a third banner and leave those two blind to an upload failure,
     * on exactly the drop most likely to go wrong.
     *
     * ⭐ `onStart` IS WHY THE HOOK HAS ONE. A drop must expand this panel: since
     * #318 it starts COLLAPSED and the root accepts drops whether or not it is
     * open, so without this the one place the result is reported is the one
     * place the reader cannot see.
     */
    const upl = useFileUpload({
        /* ⛔ THE EXPIRY LIVES IN THE HOOK so neither tray can ship without it —
           see `useFileUpload`'s note on the fourth two-tray miss. */
        autoClearMs: BANNER_MS,
        getToken,
        upload: (token, f) => uploadDocument(token, facilityCode, f, { source: 'Upload' }),
        onStart: () => { setError(null); setOpen(true); },
        /**
         * ⛔ A LAMBDA, NOT `onDone: load`. `load` is a `const` from `useCallback`
         * declared BELOW this call, so naming it directly is a temporal-dead-zone
         * error — `tsc` rejects it with TS2448/TS2454.
         *
         * ⚠️ AND IT WAS WRITTEN THAT WAY FIRST, on 2026-09-08. Neither the
         * per-file transpile nor `npm run lint` caught it; only the real
         * `npm run build` did, because it is the only step that reads the whole
         * file's bindings. ⭐ That is the argument for the build being in the
         * path to production, not the lint alone.
         *
         * ⛔ DO NOT "SIMPLIFY" THIS BACK. The arrow defers the reference to call
         * time, which is after render, by which point `load` is assigned. Moving
         * the hook below `load` would work too — the arrow is chosen because it
         * survives somebody reordering the file.
         */
        onDone: () => load(),
        onError: setError,
        failureMessage: 'Could not upload that file.',
    });

    /**
     * ---- ⛔⛔ THE "N FILES ADDED" BANNER IS A REPORT, NOT A STATE (2026-09-10) --
     *
     * > Pete: *"I file added is showing when Attached Paperwork is collapsed"* —
     * > with a screenshot of a FOLDED tray sitting above WMS Orders, wearing a
     * > green banner and pushing the whole rail down.
     *
     * ⛔ IT MUST STAY OUTSIDE THE `open` BLOCK AND THAT IS NOT THE BUG. The root
     * takes a file drop whether the section is expanded or not, and since #318 it
     * starts COLLAPSED — a banner rendered inside `{open && …}` is a report nobody
     * can see, on exactly the drop most likely to go wrong (#288). ⚠️ Gating it on
     * `open` would trade a visible nuisance for an invisible failure.
     *
     * ⭐ THE FAULT IS THAT IT NEVER LEFT. `useFileUpload` sets `added` and nothing
     * clears it until the NEXT upload, so a one-second fact occupied a permanent
     * row in a rail that is short of space. A confirmation that outlives the thing
     * it confirms stops being read at all.
     *
     * ⛔ SO IT EXPIRES, and it expires on BOTH conditions:
     *   · after `BANNER_MS`, because the report has been made;
     *   · the moment the section is OPENED, because the list itself is now the
     *     answer and the banner would be saying it twice.
     *
     * ⚠️ `upl.reset` IS STABLE (`useCallback(…, [])` in the hook), so this cannot
     * loop. The timer is cleared on unmount and on every change — a second upload
     * inside the window must restart the clock, not inherit the first one's.
     */
    /* ⛔⛔ ~~THE PRIVATE EXPIRY TIMER~~ MOVED INTO `useFileUpload` (2026-09-10) as
       `autoClearMs`. ⚠️ IT WAS PRIVATE TO THIS FILE, which is exactly why
       `ScanFolderPanel`'s identical banner had no expiry and sat under its header
       until the next upload — §4.14.3, fourth occurrence. A caller cannot forget an
       argument the way it can forget an effect.
       ⭐ The ~~clear-on-open~~ half went with it: the banner is now gated on `open`,
       so being open is the condition for SHOWING it rather than for hiding it. */
    /**
     * ⭐⭐ WHICH TRAY CARDS ARE TICKED (2026-08-27), keyed by a string that says
     * WHICH KIND of thing it is: `scan:<id>` or `doc:<id>`.
     *
     * ⛔ THE PREFIX IS NOT DECORATION. A SharePoint item id and a Dock document id
     * are different namespaces that can collide, and a collision here would tick a
     * bill of lading because an unrelated scan happened to share its id — then file
     * it somewhere nobody chose.
     */
    const [ticked, setTicked] = useState<Set<string>>(new Set());
    /**
     * ---- ⛔⛔⛔ MOVED HERE 2026-09-23 AFTER IT BLANKED THE WHOLE APP --------
     *
     * ⭐ REPORTED UP BY `PaperworkList` through `AttachedPaperworkPanel`. ⚠️ `null`
     * until it has fetched — see the header note on why that is not `0`.
     * ⛔ `setAttachedCount` IS A useState SETTER, so its identity is stable and the
     * ref-guard in PaperworkList is belt to this brace, not a substitute for it.
     *
     * ---- ⛔⛔ WHY IT IS UP HERE AND MUST STAY -------------------------------
     *
     * It used to be declared ~40 lines BELOW these two early returns:
     *
     *     if (sourceView === 'LocalFolder') return null;
     *     if (source === null) return null;
     *
     * ⛔ THAT IS A HOOK BEHIND A CONDITION, and React counts hooks per render.
     * A render that returned early registered N; the next one that did not
     * registered N+1, React threw **#310 "rendered more hooks than during the
     * previous render"**, and because nothing catches it the WHOLE TREE
     * UNMOUNTED — a blank page, not a broken panel.
     *
     * ⚠️ AND IT TOOK A FACILITY SWITCH TO SHOW IT. Within one facility `source`
     * settles once and every render agrees, so the hook count never changes.
     * Switching facility flips `source` back to null UNDER A COMPONENT THAT IS
     * ALREADY MOUNTED, which is the only path that crosses the boundary.
     *   ** A HOOK BEHIND A CONDITION IS NOT A BUG THAT HAPPENS ON EVERY RENDER.
     *      IT IS A BUG THAT HAPPENS ON THE RENDER WHERE THE CONDITION CHANGES,
     *      AND THAT RENDER MAY NEVER OCCUR IN TESTING. **
     *   ** AN EARLY RETURN IS CHEAP TO ADD AND SILENTLY REDEFINES EVERY LINE
     *      BELOW IT AS CONDITIONAL. **
     *
     * ⛔ SO NOTHING BELOW THE `return null` GUARDS MAY BE A HOOK. If this needs
     * to move again, it moves UP, never down.
     */
    const [attachedCount, setAttachedCount] = useState<number | null>(null);

    const load = useCallback(async () => {
        if (!facilityCode) return;
        setLoading(true);
        try {
            const token = await getToken();
            /**
             * ⚠️ BOTH LISTS, AND A FAILURE OF ONE MUST NOT BLANK THE OTHER. The tray
             * comes from our own database and the scan list from Graph; SharePoint
             * being unreachable is a bad morning, but it is not a reason to hide the
             * documents already in Dock waiting to be filed.
             */
            const [scanRes, unfiledRes] = await Promise.allSettled([
                listScans(token, facilityCode),
                getUnfiledDocuments(token, facilityCode),
            ]);

            if (scanRes.status === 'fulfilled') {
                setSource(scanRes.value.source);
                setReason(scanRes.value.reason);
                setTruncated(scanRes.value.truncated);
                // ⭐ Already-ingested rows are dropped HERE rather than by the server,
                // which returns them so the count can be honest — see listFacilityScans.
                setScans(scanRes.value.files.filter(f => !f.known));
                setError(null);
            } else {
                setError('Could not read the scan folder.');
            }

            if (unfiledRes.status === 'fulfilled') {
                /**
                 * ---- ⛔⛔ THE SHAREPOINT VIEW SHOWS SHAREPOINT'S FILES ---------
                 *
                 * > Pete, 2026-09-10: *"Same for Sharepoint view, only show the
                 * > files in that view."*
                 *
                 * ⭐ Every document records its own `Source`. This tray is the
                 * SharePoint pile, so a scan ingested from SharePoint belongs here
                 * and one from a local folder or a manual upload does not — those
                 * are on the other icon.
                 *
                 * ⚠️ SEE THE LONGER NOTE IN `ScanFolderPanel`: uploads had to land
                 * in ONE of the two views or become invisible, and a manual upload
                 * comes off the person's own machine.
                 */
                /**
                 * ⛔⛔ EVERY UNFILED DOCUMENT, WHATEVER SOURCE BROUGHT IT IN.
                 *
                 * > Pete, 2026-09-10: *"These files are in Sharepoint, which isn't
                 * > showing any files now????"*
                 *
                 * ⛔ THE FIRST CUT FILTERED ON `source === 'SharePoint'` AND EMPTIED
                 * THIS PILE. The only unfiled documents at Brighton are Uploads, so
                 * the "SharePoint view" showed nothing at all while the local view
                 * showed the two files Pete had just said did not belong there.
                 *
                 * ⭐ THE SPLIT IS NOT BY INGEST SOURCE, IT IS BY WHETHER DOCK HAS
                 * THE FILE. This side is everything already in Dock and waiting to
                 * be filed; the other side is the scanner's folder on the PC, which
                 * holds files Dock has never seen. See the long note in
                 * `ScanFolderPanel`.
                 */
                setUnfiled(unfiledRes.value.documents);
            }
        } finally {
            setLoading(false);
        }
    }, [facilityCode, getToken]);

    useEffect(() => { void load(); }, [load, refreshKey]);

    /**
     * ⚠️ POLLED ONLY WHILE OPEN AND IDLE. A refresh landing mid-drag would replace the
     * element under the cursor, and the browser cancels a drag whose source node is
     * removed — the scheduler would see the document snap back for no visible reason.
     */
    useEffect(() => {
        if (!open || busy) return;
        const t = window.setInterval(() => { void load(); }, POLL_MS);
        return () => window.clearInterval(t);
    }, [open, busy, load]);

    /**
     * ⛔ THIS IS NO LONGER WHAT CLICKING PREVIEW DOES — it is the FALLBACK behind it.
     * The panel draws PDFs and raster images; a .docx or a .tif is real paperwork it
     * cannot draw, and this is the way out that the panel offers instead.
     *
     * ⚠️ AND IT IS STILL AN `attachment` LINK, deliberately. Nothing about the storage
     * account's headers changed to make the panel work; see lib/previewTypes.ts.
     */
    async function openDocumentInTab(documentId: string) {
        try {
            const { url } = await getDocumentUrl(await getToken(), documentId);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch {
            setError('Could not open that document.');
        }
    }

    /**
     * ---- ⛔⛔ SEND ONE STORED DOCUMENT TO THE LABEL PRINTER ----------------
     *
     * ⚠️ THE SERVER READS THE BYTES, NOT THE BROWSER. The two obvious reuses
     * are both wrong: `getDocumentUrl` hands back a storage SAS stamped
     * `attachment` whose CORS for this origin nobody has verified, and the
     * preview route refuses a `.zpl` on purpose — its allow-list is about what
     * may be DRAWN in an iframe, not what may be PRINTED.
     *
     * ⛔ THE ^XA/^XZ CHECK HAPPENS TWICE, WHICH IS NOT REDUNDANT. The server
     * checks the FILE; `printLabel` checks what is actually about to go down
     * the wire. A 415 from the first is a bad upload; a refusal from the second
     * would be a bug in us.
     */
    async function printDocument(d: DocumentDto) {
        setError(null);
        setPrinted(null);
        try {
            const { zpl } = await getPrintContent(await getToken(), d.documentId);
            /* ⭐ `documentType` picks the printer, from dbo.PrintRoute. A tray
               document has none yet — it is unfiled — so 'Label' is the route a
               raw ZPL file takes. Runbook B3: an unrouted type falling back to
               the dialog is the CORRECT default, not a fault. */
            const outcome = await zebra.printLabel({
                documentType: d.documentType ?? 'Label',
                zpl,
            });
            /* ⛔ BOTH BRANCHES SAY SOMETHING, and `ok: false` is very often not
               an error — "no label printer configured at this site" is a
               configuration answer. It goes in the neutral line; only a thrown
               failure below is painted as a fault. */
            setPrinted(outcome.message);
        } catch (err) {
            setError(err instanceof ApiCallError
                ? err.details.error
                : 'Could not read that document to print it.');
        }
    }

    /**
     * ⚠️ WHICH ROWS GET THE BUTTON, AND WHY IT IS NOT ALL OF THEM.
     *
     * ⛔ A ZEBRA HANDED A PDF DOES NOT REFUSE IT — it consumes it as commands
     * and feeds label stock until somebody physically stops it. The server
     * refuses that with a 413/415, but a button offered on every row is an
     * invitation to find out, and the person finding out is at the printer.
     *
     * ⭐ SO THE EXTENSION IS THE GATE. ⚠️ It is a HINT, not the control — the
     * `print-content` route still checks the bytes.
     */
    const isLabelFile = (fileName: string) => /\.zpl$/i.test((fileName ?? '').trim());

    /**
     * ⭐ LOOK AT A SCAN BEFORE DECIDING WHAT IT IS. Pete, 2026-08-25, staring at a tray
     * holding `image001.png` and a file called `Name`.
     *
     * ⛔ THIS IS WHY THERE IS NO RENAME. Renaming a scan in SharePoint would be work
     * thrown away — filing it onto a load renames it to the site's template anyway. The
     * need behind "can I rename it" was "what IS this", and that is a preview.
     */
    async function openScanInTab(itemId: string) {
        try {
            const { url } = await getScanPreviewUrl(await getToken(), facilityCode, itemId);
            window.open(url, '_blank', 'noopener,noreferrer');
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not open that scan.');
        }
    }

    /**
     * ⭐ THE FALLBACK IS CHOSEN BY WHAT IS OPEN, not by a second decision. Whichever
     * kind of thing the panel is showing, the Download button on it opens that same
     * thing the old way.
     */
    function downloadWhatIsOpen() {
        if (!viewing) return;
        if (viewing.target.kind === 'document') void openDocumentInTab(viewing.target.documentId);
        else void openScanInTab(viewing.target.itemId);
    }

    /**
     * ⛔ RECYCLE BIN, NOT ERASE — and the dialog says so.
     *
     * ⚠️ A scheduler pressing this is clearing rubbish, not making an archival decision,
     * so the wording has to make the reversibility obvious. Anything that reads as
     * "destroy forever" makes people leave junk in the tray instead.
     */
    async function runDelete() {
        if (!confirmDelete) return;
        setDeleting(true);
        try {
            await deleteScan(await getToken(), facilityCode, confirmDelete.id);
            setConfirmDelete(null);
            await load();
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not delete that file.');
            setConfirmDelete(null);
        } finally {
            setDeleting(false);
        }
    }

    /**
     * Take an already-stored document out of the tray.
     *
     * ⚠️ THE REASON IS CANNED, AND THAT IS NOT LAZINESS. The server demands one because
     * "why is this BOL not on the load any more?" gets asked later — but that question
     * is about a document being pulled OFF a load. This one was never on anything, and
     * the honest reason is exactly the button's own meaning. Making a scheduler type
     * "not paperwork" forty times a week would produce forty typos, not forty reasons.
     *
     * ⭐ WHO AND WHEN COME FROM THE SERVER, which stamps the actor from the token. The
     * canned text is the WHY; it is not standing in for the other two.
     */
    async function runVoid() {
        if (!confirmVoid) return;
        setVoiding(true);
        try {
            await voidDocumentApi(
                await getToken(), confirmVoid.documentId,
                'Not paperwork - removed from the paperwork tray.',
            );
            setConfirmVoid(null);
            await load();
        } catch (err) {
            setError(err instanceof ApiCallError ? err.details.error : 'Could not remove that document.');
            setConfirmVoid(null);
        } finally {
            setVoiding(false);
        }
    }

    /**
     * ---- ⭐ A FILE DRAGGED IN FROM FILE EXPLORER --------------------------------
     *
     * Pete, 2026-08-25: *"Or drop/drag a document into the Paperwork from File
     * Explorer"*.
     *
     * ⛔ THIS WAS ALSO A REGRESSION FIX. The only two upload-from-PC paths in the app
     * lived in ScanFolderPanel, and hiding that panel on SharePoint sites left a
     * facility with NO way to get a file off a desktop into Dock.
     *
     * ⭐ IT LANDS IN DOCK, NOT IN SHAREPOINT. The SharePoint folder is the SCANNER's
     * drop point; a person dragging from their own machine is a different act, and
     * `storeDocument` already has a path for it (`source: 'Upload'`). Uploading to
     * SharePoint first would mean Dock writing new files into their library, which is a
     * much bigger permission story for no benefit.
     */
    /**
     * ⛔⛔ `uploadFiles` MOVED OUT ON 2026-09-08 — see `lib/useFileUpload.ts`.
     *
     * ⭐ The whole #288 write-up moved WITH it, unabridged: the live
     * `DataTransfer.files`, the MSAL await that emptied it, and why every symptom
     * — no request, no throw, no movement on screen — followed from one line
     * sitting on the wrong side of an `await`. ⚠️ It is not repeated here,
     * because two copies of an explanation drift exactly the way two copies of
     * the code did.
     *
     * ⛔ THE NOTE THAT USED TO SIT HERE said `ScanFolderPanel` "has the identical
     * await-then-iterate shape and has never shown this — because it clears the
     * input in `finally`". ⚠️ That is history in two ways now: the shape is
     * shared, and that panel's `finally` went when the snapshot made it
     * redundant.
     */

    /**
     * ⚠️ ONLY A FILE DROP, NEVER THE TRAY'S OWN DRAG. A card being dragged WITHIN the
     * app carries the paperwork MIME type; a file from Explorer carries 'Files'. Without
     * this test, picking up a scan and letting go over the tray would try to upload it.
     */
    const isFileDrop = (e: React.DragEvent) => isFileDropDt(e.dataTransfer);

    /**
     * ⛔ SILENT ON A LOCAL-FOLDER SITE, AND SILENT UNTIL WE KNOW WHICH IT IS.
     *
     * ⚠️ `source === null` MEANS THE FIRST READ HAS NOT COME BACK. Rendering a header
     * before then would flash a Paperwork section onto a site that has its own one two
     * inches below — briefly showing two, which is the confusion this move exists to
     * end.
     */
    /**
     * ⛔⛔ §1R.16 REVERSED — see the long note in `ScanFolderPanel`. `sourceView`
     * is the scheduler's live choice; `ScanSourceType` only decides which view
     * opens first.
     *
     * ⚠️ `source === null` STILL MEANS "THE FIRST READ HAS NOT COME BACK", and
     * that guard survives the reversal: rendering before we know would flash a
     * second Paperwork section onto the rail.
     */
    if (sourceView === 'LocalFolder') return null;
    if (source === null) return null;

    const total = scans.length + unfiled.length;

    /** Stable keys for the tick set. See the note on `ticked`. */
    const scanKey = (id: string) => `scan:${id}`;
    const docKey = (id: string) => `doc:${id}`;

    const toggle = (key: string) => setTicked((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key); else next.add(key);
        return next;
    });

    /**
     * ⭐ EVERY TICKED CARD, IN THE ORDER THE TRAY SHOWS THEM.
     *
     * ⚠️ REBUILT FROM THE CURRENT LISTS, NOT FROM THE TICK SET ALONE. A scan that
     * has been ingested or a document somebody voided disappears from the tray, and
     * its key can linger in the set — filing a row that is no longer there would
     * fail for a reason the scheduler cannot see on screen.
     */
    const tickedItems = (): PaperworkDragItem[] => {
        const out: PaperworkDragItem[] = [];
        for (const sc of scans) {
            if (ticked.has(scanKey(sc.id))) out.push({ kind: 'scan', itemId: sc.id, name: sc.name });
        }
        for (const d of unfiled) {
            if (ticked.has(docKey(d.documentId))) {
                out.push({ kind: 'document', documentId: d.documentId, name: d.fileName });
            }
        }
        return out;
    };
    const tickedCount = tickedItems().length;
    /* ⛔⛤ ~~const [attachedCount, setAttachedCount] = useState(null)~~ — MOVED UP
       2026-09-23. It was declared HERE, forty lines below
       `if (source === null) return null;`, and a facility switch therefore
       changed this component's hook count mid-life and threw React #310, which
       unmounted the whole tree and blanked the page. The declaration now sits
       beside `ticked`, in the unconditional region; the full account is there.
         ** AN EARLY RETURN IS CHEAP TO ADD AND SILENTLY REDEFINES EVERY LINE
            BELOW IT AS CONDITIONAL. **
       ⚠️ NOTHING BELOW THIS POINT IN THE FUNCTION MAY BE A HOOK. */

    /**
     * ⛔ WHAT A DRAG STARTING ON THIS CARD CARRIES.
     *
     * ⭐ A ticked card drags the whole selection; an unticked one drags only itself
     * — the desktop-file-manager rule. See the note at `onDragStart`.
     */
    const dragItemsFor = (key: string, self: PaperworkDragItem): (() => PaperworkDragItem[]) =>
        () => (ticked.has(key) ? tickedItems() : [self]);


    return (
        <div
            ref={trayRef}
            className={'flex min-h-0 flex-col border-t ' + (dropping ? 'bg-sky-50 ring-2 ring-inset ring-sky-400' : '')}
            onDragOver={(e) => {
                if (!isFileDrop(e)) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'copy';
                setDropping(true);
            }}
            onDragLeave={(e) => {
                // ⚠️ Only when the pointer really leaves the panel — dragging across a
                // child fires dragleave on the parent and would flicker the highlight.
                if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                setDropping(false);
            }}
            onDrop={(e) => {
                if (!isFileDrop(e)) return;
                e.preventDefault();
                setDropping(false);
                /* ⛔ SNAPSHOT INSIDE THE EVENT. The hook snapshots too; the belt
                   is the one that has already failed once (#288). */
                void upl.run(snapshotFiles(e.dataTransfer.files));
            }}
        >
            {/**
              * ---- ⭐⭐ THE HEADER IS A ROW, NOT A BUTTON (2026-09-10) -------------
              *
              * > Pete: *"the Sharepoint cloud icon and the This machine icon should be
              * > on the scanned Paperwork panel"* … *"I thought these were gonna be
              * > icons on the panel itself, not below it? Also, don't see the toggle."*
              *
              * ⛔ THE TOGGLE COULD NOT LIVE IN THE HEADER WHILE THE HEADER WAS A
              * `<button>` — a button inside a button is invalid HTML and the browser
              * hoists the inner one out, swallowing its click. So it was put in the
              * body instead, where it was invisible whenever the section was folded
              * (which is its default, #318) **and** invisible when the tray was empty,
              * because it sat inside the `total > 0` branch. ⚠️ Both of those are
              * exactly the moments a scheduler wants to switch source: nothing is
              * showing, so they want to look somewhere else.
              *
              * ⭐ THE FIX IS STRUCTURAL, NOT COSMETIC: the header is now a flex ROW,
              * the expand/collapse is a button INSIDE it, and the toggle is its
              * SIBLING. Neither nests in the other, so both are always clickable and
              * the toggle is visible whether the section is open, closed or empty.
              */}
            <div
                className={`shrink-0 bg-card flex w-full items-center gap-2 px-3 py-2 ${SECTION_HEADER}`
                                    /* ⛔ ~~`bg-cyan-50` WHILE ATTACHED~~ REVERTED with the title colour
                                       (Pete, 2026-09-10). ⚠️ `bg-card` STAYS and is not decoration:
                                       SECTION_HEADER is semi-transparent, and the drop tray's highlight
                                       shows straight through an untinted header. */}
            >
            <button
                type="button"
                onClick={() => setOpen(o => !o)}
                /**
                 * ⭐⭐ NOT STICKY ANY MORE — IT IS OUTSIDE THE SCROLLER (2026-08-27).
                 *
                 * Pete: *"when you scroll those sections, the 'Scanned Paperwork' title
                 * bar should not disappear."* ⛔ It WAS `sticky top-0`, and that is
                 * exactly why it went: sticky is a promise about ONE scroll container,
                 * and this rail nests three. The header pinned to whichever ancestor was
                 * scrolling and rode away the moment a wheel event chained to another.
                 *
                 * ⭐ Now the root is a flex column, this is `shrink-0`, and only the body
                 * below scrolls — a structural guarantee, not a CSS promise.
                 *
                 * ⛔ bg-card stays: SECTION_HEADER is semi-transparent and the drop tray
                 * would show through it.
                 */
                className="flex min-w-0 flex-1 items-center gap-2 text-left hover:opacity-80"
            >
                {/**
                  * ⛔⛔ `shrink-0` IS NOT DECORATION — WITHOUT IT THE CHEVRON VANISHES.
                  *
                  * > Pete, 2026-09-10: *"Also lost its chevron."* … and again after the
                  * > first fix: *"chevron still not there."*
                  *
                  * ⚠️ THE ICONS WERE PRESENT BOTH TIMES. This button is `flex-1` inside a
                  * header row that also holds the source toggle, and a flex item's
                  * DEFAULT is `min-width: auto` with `flex-shrink: 1` — so when the row
                  * ran out of width the browser crushed the two 14px icons to zero and
                  * wrapped "Scanned Paperwork" onto two lines instead. ⛔ That is exactly
                  * what Pete's screenshot shows, and it is why "the chevron is in the
                  * code" was true and useless.
                  */}
                {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                {/**
                  * ---- ⭐⭐ THE TWO MODES DO NOT LOOK ALIKE (2026-09-10) ---------
                  *
                  * > Pete: *"lets leave the color of Scanned Paperwork and its icon
                  * > the same it is now but lets change the color of Attached
                  * > Paperwork to a new color (permanent) and lets put a Paperclip on
                  * > the Attached Paperwork ICon so its different than Scanned
                  * > Paperwork. Maybe we use a different icon for the scanned
                  * > paperwork - seems to similiar to others."*
                  *
                  * ⛔ ONE PANEL, TWO MODES, AND UNTIL NOW ONE APPEARANCE. Both drew
                  * `FileText` in the default foreground and changed only the words —
                  * so a scheduler who glanced at the rail could not tell whether they
                  * were looking at the QUEUE or at ONE LOAD'S dossier. ⚠️ Those two
                  * answer different questions and dragging into the wrong one files
                  * paperwork on the wrong thing.
                  *
                  * ⭐ THE PAPERCLIP IS NOT AN ARBITRARY PICK. It is already the
                  * paperwork mark on every arrival card's tool strip, so "paperclip =
                  * what is attached to this load" is a word the app already speaks.
                  *
                  * ⛔ AND `FileText` WENT because Pete is right that it read as
                  * generic — `FilePen` is WMS Orders two panels down and the two are
                  * a glance apart. `Inbox` says what this pile actually is: a work
                  * queue things arrive in and leave from.
                  *
                  * ⚠️ CYAN, NOT VIOLET OR EMERALD. Violet is the dedication colour on
                  * the grid (`bg-violet-600`) and emerald means SIGNED on every
                  * document row; reusing either would make a mode look like a status.
                  * Cyan is unused anywhere in this app.
                  */}
                    {/**
                      * ---- ⛔⚤ THE TITLE ICON IS A DOCUMENT NOW (Pete, 2026-09-20) --
                      *
                      * > *"The Paperwork title gets the document icon to its left (just
                      * > like the other panels)"* — and, in the same breath, *"the
                      * > sentence under the attachment gets the paperclip/attach icon,
                      * > not the document icon."*
                      *
                      * ⛔⛤ ~~<Paperclip className="… text-cyan-700" />~~ on the title.
                      * ⭐⭐ THE PAPERCLIP DID NOT LEAVE, IT MOVED DOWN ONE LEVEL —
                      * `PaperworkList` renders it in place of the suppressed "signing
                      * and removed-document history" sentence. Which is a promotion for
                      * the symbol, not a demotion: it now marks the one thing that is
                      * ATTACHED-but-elsewhere rather than sitting on a panel name.
                      *
                      * ⛔⛔ AND IT OVERRULES A 2026-09-10 ARGUMENT THAT IS STILL QUOTED
                      * TWENTY LINES UP. That day `FileText` was dropped for reading
                      * "generic", with `FilePen` on WMS Orders a glance away. ⚠️ Pete
                      * has now asked for exactly the icon that argument rejected, and
                      * his reason beats it: the other four rails all wear a plain mark
                      * of WHAT THEY ARE, and this panel was the only one wearing a mark
                      * of what it DOES.
                      *   ** AN ICON CHOSEN TO BE DISTINCTIVE AND AN ICON CHOSEN TO
                      *      MATCH ITS NEIGHBOURS ARE TWO DIFFERENT JOBS. THE ROW OF
                      *      PANELS DECIDES WHICH ONE IS BEING DONE. **
                      * ⚠️ `FilePen` (WMS Orders) AND `FileText` ARE STILL A GLANCE
                      * APART. That cost is real and is now paid deliberately — the
                      * cyan colour is what separates them, and it stays.
                      *
                      * ⛔ SCANNED MODE KEEPS `Inbox`, UNTOUCHED. It is a work queue,
                      * not a dossier, and nothing Pete asked for touches it.
                      */}
                {trayView === 'Attached'
                    ? <FileText className="h-3.5 w-3.5 shrink-0 text-cyan-700" />
                    : <Inbox className="h-3.5 w-3.5 shrink-0" />}
                {/**
                  * ---- ⭐⭐ THE PANEL IS NAMED FOR WHAT IT IS SHOWING --------------
                  *
                  * > Pete: *"Thats why the panel needs to change names and have a toggle
                  * > button."*
                  *
                  * ⛔ AND THE SOURCE WORD LIVES HERE NOW. The toggle is icons only at
                  * Pete's request, so this header is what discharges §1R.16's
                  * requirement that the active source be STATED — see the long note in
                  * `ScanSourceToggle`. Removing this word is not a cosmetic edit.
                  */}
                {/**
                  * ⛔⛔ ~~"· SharePoint" / "· This machine"~~ REMOVED (Pete, 2026-09-10):
                  * *"remove 'This Machine', not necessary - no wording at all in the
                  * panel."*
                  *
                  * ⚠️⚠️ THIS FINISHES UNDOING §1R.16's SAFEGUARD AND THAT IS WORTH
                  * WRITING DOWN. The rule was reversed on the argument that a
                  * scheduler switching source is safe **because the screen STATES
                  * which source it is showing** — first on the toggle, then, when the
                  * toggle went to icons, here. Both are now gone: what identifies the
                  * active source is a highlighted icon and its tooltip.
                  *
                  * ⛔ SO THE PROVENANCE ARGUMENT NOW RESTS ENTIRELY ON THE DATA, not
                  * on the screen: every document still records its own `Source`, and
                  * that is what a dispute is answered from. ⚠️ What is lost is the
                  * at-a-glance check — a scheduler who misreads which icon is lit can
                  * believe scans are missing when they are simply in the other pile.
                  * That is Pete's call, made knowingly, and it is recorded here rather
                  * than quietly dropped.
                  */}
                {/**
                  * ---- ⭐⭐ THE NAME WEARS THE ICON'S COLOUR (Pete, 2026-09-10) -----
                  *
                  * > *"lets try having the panel name be the same color as the icon. It
                  * > might help distinquish the sections from one another. For example,
                  * > the word label will be purple to match the icon color for labor."*
                  *
                  * ---- ⚠️ THIS REINSTATES WHAT WAS STRUCK EARLIER THE SAME DAY -------
                  *
                  * ⛔ `text-cyan-700` WAS ON THIS TITLE AND PETE REMOVED IT: *"the actual
                  * words, should still be the same color black as the other panels."*
                  * ⭐ AND THAT WAS NOT A CONTRADICTION OF THIS. The objection was to ONE
                  * panel wearing a colour while four others did not — a header that
                  * looked broken rather than categorised. Colouring every name to match
                  * its own glyph is the system that reading was missing.
                  *
                  * ⚠️ SO THE RULE IS "MATCH THE GLYPH", NOT "BE COLOURFUL". `Scanned
                  * Paperwork` stays default BECAUSE its `Inbox` glyph is uncoloured; a
                  * title that invented a colour its icon does not have would put the
                  * panel back in the state Pete rejected.
                  */}
                    {/**
                      * ---- ⛔⚤ `Paperwork (ALL)` / `Paperwork (TRAFFIX)` -----------
                      *
                      * > Pete, 2026-09-20: *"Too much going on for Attached Paperwork.
                      * > Change it to just 'Paperwork'. Use the same format as other
                      * > panels Paperwork (all) or Paperwork (traffix)."*
                      *
                      * ⛔⛤ ~~'Attached Paperwork'~~ — the word ATTACHED went because it
                      * described the MODE, and the mode is already stated by the icon
                      * beside it and the toggle to its right. Three statements of one
                      * fact, and the one thing the header never said was WHOSE
                      * paperwork.
                      *
                      * ⭐⭐ `focusScopeLabel` IS THE SAME FUNCTION THE FOUR RAILS CALL.
                      * Not a lookalike string built here — that is how the rails' label
                      * drifted into two shapes on 2026-09-19, and the fix was to have
                      * one function own the wording.
                      *   ** IDENTICAL WORDING MAINTAINED BY COPYING IS WORDING THAT
                      *      DIVERGES. **
                      *
                      * ⚠️ SCANNED MODE IS NOT SCOPED AND DOES NOT PRETEND TO BE. The
                      * scan folder is a facility-wide pile with no client, carrier or
                      * load attached to it yet — that is the definition of the queue.
                      * A `(TRAFFIX)` on it would claim a filter that does not exist.
                      */}
                {/**
                  * ---- ⛔⛤ THE TITLE STOPS EATING ITS OWN SCOPE (2026-09-22) ----
                  *
                  * > Pete, with a screenshot of `Paperwork (Appt - A0…`:
                  * > *"needs to be formatted the same way as (app - ) on History"*
                  *
                  * ⛔⛤ ~~`Paperwork ${focusScopeLabel(focus)}` inside one truncating
                  * `<span className="truncate text-sm font-medium">`~~ — the scope
                  * was part of the TITLE STRING, so `truncate` cut the appointment
                  * number in half and the header said `(Appt - A0…`.
                  *   ** A LABEL CONCATENATED INTO A TRUNCATING STRING IS A LABEL
                  *      THAT DISAPPEARS EXACTLY WHEN IT IS LONGEST — WHICH IS WHEN
                  *      IT MATTERS. **
                  *
                  * ⭐ TWO SPANS, COPIED FROM `HistoryPanel.tsx:211-228` rather than
                  * invented: the NAME is bold and truncates, the SCOPE is small,
                  * muted and `shrink-0` so it never truncates at all. That is what
                  * the other four rails already do, and the ask was literally "the
                  * same way as History".
                  *
                  * ⚠️ SCANNED MODE STILL SHOWS NO SCOPE, unchanged — the scan folder
                  * is a facility-wide pile with nothing attached to it yet, and a
                  * `(TRAFFIX)` on it would claim a filter that does not exist.
                  */}
                <span className={`truncate text-sm font-medium${
                    trayView === 'Attached' ? ' text-cyan-700' : ''}`}>
                    {trayView === 'Attached' ? 'Paperwork' : 'Scanned Paperwork'}
                </span>
                {trayView === 'Attached' && (
                    <span
                        className="shrink-0 self-center text-[10px] leading-none text-muted-foreground"
                        title={`Showing ${focusScopeLabel(focus)}`}
                    >
                        {focusScopeLabel(focus)}
                    </span>
                )}
                {(loading || busy || upl.busy) && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
                {/**
                  * ⭐ THE COUNT IS SHOWN AT ZERO TOO. "0 waiting" and a missing badge
                  * look the same at a glance, and only one of them means the folder has
                  * been read successfully.
                  */}
                {/**
                  * ⭐⭐ THE TICK COUNT SITS IN THE HEADER, WHICH IS THE ONLY PLACE IT CAN
                  * BE SEEN WHEN THE SECTION IS FOLDED (2026-08-27).
                  *
                  * ⚠️ Since #318 this panel starts collapsed. A selection that exists but
                  * is invisible is a selection somebody will drag by accident three
                  * minutes later — so if anything is ticked, the header says so and the
                  * count is the control that clears it.
                  *
                  * ⛔ NOT A `<button>` — this header IS a button, and a button inside a
                  * button is invalid HTML: the browser hoists it out and the inner click
                  * is swallowed. That mistake has already been made once in this rail
                  * (see LabourPanel's header note), so it is a span with a click handler.
                  */}
                {tickedCount > 0 && (
                    <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); setTicked(new Set()); }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault(); e.stopPropagation(); setTicked(new Set());
                            }
                        }}
                        title="Clear the selection"
                        className="ml-auto cursor-pointer rounded bg-primary/15 px-1.5 text-xs font-medium
                                   text-primary hover:bg-primary/25"
                    >
                        {tickedCount} ticked · clear
                    </span>
                )}
                {/* ⛔ ~~"{total} waiting" WAS HERE~~ and is gone (Pete, 2026-09-10:
                    *"don't need the 2 waiting, remove it"*). ⚠️ The count still rides on
                    the way back OUT of attached mode, which is the one moment it must
                    not be missable — see `AttachedPaperworkPanel`. */}
            </button>
            {/**
              * ---- ⭐⭐ THREE ICONS, ON EVERY VIEW (Pete, 2026-09-10) ------------
              *
              * > *"need one more icon for the toggle between Scanned paperwork and
              * > Attached Paperwork. Right next to the Sharepoint icon."* …
              * > *"Remove the SCanned 2 button. It should be the 3 icons on every
              * > view period."*
              *
              * ⛔ ~~THE "← Scanned N" CHIP~~ IS GONE. It only existed while attached,
              * so it was a way BACK with no matching way IN — the only route to the
              * attached view was to hover something and hope. Three peers that never
              * move is one model; a chip that appears and a toggle that disappears
              * was two half-models sharing a corner.
              *
              * ⛔ SIBLING OF THE COLLAPSE BUTTON, NEVER A CHILD: a button inside a
              * button is hoisted out by the browser and the inner click is swallowed.
              */}
            <ScanSourceToggle
                value={trayView}
                onChange={onTrayView}
                attachedAvailable={attached !== null}
                onDropAttached={onDropAttached}
                onDropUnfile={onDropUnfile}
                onDropLocal={onDropLocal}
            />
            {/* ⭐ UPPER RIGHT, AFTER THE TOGGLE — Pete, 2026-09-10: *"Need that
                little refresh button in upper right hand corner"*. ⛔ The two
                copies that lived in the BODY are gone: they existed only while the
                section was expanded AND in the scanned view, which is not where a
                scheduler is standing when they want one. */}
                {/**
                  * ---- ⭐⭐ THE HEADER COUNT (Pete, 2026-09-22) ------------------
                  *
                  * > *"There also should be a number (like all the other panels) next
                  * > to the refresh button saying how many documents in which ever of
                  * > the 3 paperwork icons is selected."*
                  *
                  * ⭐ IT FOLLOWS THE SELECTED ICON, which is what "whichever of the 3"
                  * means: attached mode counts THIS LOAD's documents, scanned mode counts
                  * the pile waiting to be filed.
                  *
                  * ⛔⛔ `null` RENDERS NOTHING, AND THAT IS NOT A LAYOUT CHOICE. The
                  * attached count is unknown until `PaperworkList` has fetched — and a
                  * `0` on a load whose paperwork has not been read yet asserts the load
                  * has none. That is the one thing this header must never say by
                  * accident, and it is the same rule HistoryPanel records for its own
                  * count.
                  *   ** A COUNT OVER AN UNASKED QUESTION IS NOT ZERO, IT IS NOTHING.
                  *      THE TWO RENDER THE SAME AND MEAN OPPOSITE THINGS. **
                  *
                  * ⚠️ SAME CLASSES AND SAME SLOT AS THE OTHER RAILS, deliberately — a
                  * count that sits two pixels off from Labor's is a count somebody
                  * reads as a different kind of number.
                  */}
                {(trayView === 'Attached' ? attachedCount : total) !== null && (
                    <span
                        className="shrink-0 self-center text-xs text-muted-foreground"
                        title={trayView === 'Attached'
                            ? 'Documents filed on this appointment'
                            : 'Scans and documents waiting to be filed'}
                    >
                        {trayView === 'Attached' ? attachedCount : total}
                    </span>
                )}
            <TrayRefreshButton
                onClick={() => void load()}
                busy={loading}
                title="Look again for new scans"
            />
            </div>

            {/**
              * ⛔⛔ THE UPLOAD RESULT SITS **OUTSIDE** THE `open` BLOCK (#288).
              *
              * ⚠️ The root accepts a file drop whether this section is expanded or not,
              * and since #318 it starts COLLAPSED — so a banner rendered inside `{open}`
              * is a report nobody can see, on exactly the drop most likely to go wrong.
              * The hook's `onStart` also expands the section, and this is the belt to that brace.
              */}
            {/**
              * ---- ⛔⛔ THE SUCCESS BANNER IS GATED ON `open`. THE ERROR IS NOT. ----
              *
              * > Pete, 2026-09-10: *"1 file added is showing when Attached Paperwork
              * > is collapsed"* — with a screenshot of a green row under a folded
              * > header, pushing WMS Orders, Labor and Communications down.
              *
              * ⛔ THE NOTE ABOVE ARGUED THIS SHOULD BE OUTSIDE THE FOLD, and its
              * reasoning is sound — but READ WHAT IT IS ABOUT: *"a report nobody can
              * see, on exactly the drop most likely to go wrong."* ⭐ **THAT IS AN
              * ARGUMENT ABOUT THE FAILURE, AND THE FAILURE HAS ITS OWN RENDERING
              * TWELVE LINES DOWN** (`error && !open`). The success count inherited a
              * safety argument that was never about it.
              *
              * ⚠️ A CONFIRMATION AND A WARNING ARE NOT THE SAME OBLIGATION. Nobody
              * needs to be told, from behind a folded panel, that the thing they just
              * did worked; somebody very much needs to be told it did not. ⛔ So the
              * asymmetry stays and is deliberate: gating BOTH would restore #288's
              * bug, gating NEITHER is what Pete photographed.
              *
              * ⭐ AND `onStart` EXPANDS THE SECTION, so a drop onto a collapsed panel
              * opens it and the count is read there — then expires on its own through
              * the hook's `autoClearMs`, in both trays, by construction.
              */}
            {open && upl.added !== null && (
                <div className="mx-2 mb-1.5 mt-1.5 flex items-start gap-1.5 rounded border border-emerald-300
                                bg-emerald-50 px-2 py-1 text-[11px] text-emerald-900">
                    <Check className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{upl.added} file{upl.added === 1 ? '' : 's'} added.</span>
                </div>
            )}
            {error && !open && (
                <div className="mx-2 mb-1.5 mt-1.5 flex items-start gap-1.5 rounded border border-amber-400
                                bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
                    <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                    <span>{error}</span>
                </div>
            )}

            {/* ⭐ ONLY THE BODY SCROLLS — see the note on the header above. */}
            {/**
              * ---- ⛔⛔ ATTACHED MODE IS **NOT** GATED ON `open` (2026-09-10) ------
              *
              * > Pete: *"the hover isn't working, changing to attached Paperwork and
              * > showing the 1 doc on that appointment."*
              *
              * ⛔ IT WAS `{open && attached && …}`, AND THAT IS WHY. Since #318 this
              * section starts COLLAPSED, so hovering a load set `attached`, the prop
              * arrived, the component re-rendered — and drew nothing. Every link in
              * the chain was correct and the feature was invisible.
              *
              * ⚠️ THE CHECK THAT SAID IT WAS WIRED WAS MORE CONFIDENT THAN WHAT IT
              * SCANNED (§4.14): it followed the prop from `GanttCalendar`'s dwell
              * timer to `Calendar`'s state to this file's parameter list, and never
              * asked whether the branch that renders it could be reached.
              *
              * ⭐ HOVERING A LOAD IS AN EXPLICIT REQUEST TO SEE ITS PAPERWORK, so it
              * opens the tray. Leaving the hover restores whatever `open` was — the
              * unfiled pile is not unmounted behind this, so nothing refetches.
              */}
                        {/**
              * ---- ⛔⛔ ATTACHED MODE IS GATED ON `open` AGAIN, AND OPENS ITSELF --
              *
              * > Pete, 2026-09-10: *"Attached paperwork is collapsing too far up.
              * > should only goto the bottom of Driver Checkin... ish"* — with a
              * screenshot showing the chevron CLOSED and the list rendering anyway.
              *
              * ⛔ I UNGATED THIS FROM `open` TO FIX "hover does nothing", AND THAT WAS
              * THE SHORTCUT. The real fault was that hovering set state and nothing
              * OPENED the section; drawing the body outside the fold papered over it
              * and cost two things at once — the chevron stopped meaning anything, and
              * the body escaped the rail's sizing, so it grew until the driver list
              * was squeezed to nothing.
              *
              * ⭐ SO THE HOVER OPENS THE SECTION (see the effect on `attached`), and
              * the body renders inside the normal open-gated flow like every other
              * panel. ⚠️ The rail's `min-h-[6rem]` on the driver list and the bounded
              * panel height then apply — which is exactly Pete's "to the bottom of
              * Driver Check-Ins, ish".
              */}
            {open && trayView === 'Attached' && attached && (
                <div className="min-h-0 overflow-y-auto px-2 pb-2">
                    {/**
                      * ⛔⛔ THE TRAY'S SECOND MODE. The unfiled pile is NOT unmounted
                      * behind this — it is simply not drawn — so coming back costs no
                      * refetch and the count on the back chip is the live one.
                      * ⚠️ `waitingCount` is what the tray actually holds, not a
                      * guess: scans still in the source plus documents with no home.
                      */}
                    <AttachedPaperworkPanel
                        target={attached}
                        facilityCode={facilityCode}
                        getToken={getToken}
                        onChanged={() => { void load(); }}
                        onSign={(d) => setSigning(d)}
                        onPrint={(d) => void printPaper(d)}
                        onCount={setAttachedCount}
                    />
                </div>
            )}

            {/* ⛔ `trayView`, NOT `!attached`. The target is REMEMBERED after you leave
                the attached view — that is what keeps the third icon live — so
                gating on it would blank the scans the moment anything had ever
                been hovered. */}
            {open && trayView !== 'Attached' && (
                <div className={'min-h-0 overflow-y-auto px-2 pb-2 ' + (busy ? 'pointer-events-none opacity-50' : '')}>
                    {(error || reason) && (
                        <div className="mb-1.5 flex items-start gap-1.5 rounded border border-amber-400 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
                            <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
                            <span>{error ?? reason}</span>
                        </div>
                    )}
                    {/* ---- ⛔⛔ THE PRINT OUTCOME, AND IT IS NOT AMBER --------
                        ⚠️ MOST OF WHAT LANDS HERE IS NOT A FAULT. "No label printer
                        is configured for Label at this site" is the CONFIGURED
                        answer (runbook B3), and "this machine has no printer called
                        X" names its own fix. ⭐ Painting all of it as a warning
                        trains people to ignore the line that also carries "Sent to
                        Dock label printer" — the one confirmation that a silent
                        print actually happened. */}
                    {printed && (
                        <div className="mb-1.5 flex items-start justify-between gap-2 rounded border
                                        bg-muted/40 px-2 py-1 text-[11px]">
                            <span>{printed}</span>
                            <button
                                type="button"
                                onClick={() => setPrinted(null)}
                                className="shrink-0 underline"
                            >
                                dismiss
                            </button>
                        </div>
                    )}

                    {total === 0 ? (
                        <div className="px-1 py-1.5 text-[11px] text-muted-foreground">
                            {/* ⛔ ~~a second Refresh button~~ — it is on the header now,
                                where it is also reachable when this branch is not
                                rendering at all. */}
                            <span>Nothing waiting. Scans appear here within a few seconds.</span>
                        </div>
                    ) : (
                        <>
                            {/* ⛔ ~~THE TOGGLE WAS HERE~~ and moved ONTO THE HEADER ROW
                                (2026-09-10). It was in the body because the header was a
                                <button> and nesting one inside it is invalid HTML; the
                                header is a flex row now, so that constraint is gone. ⚠️ It
                                also sat inside this `total > 0` branch, so an empty tray
                                offered no way to look at the other source — see the header
                                note. */}
                            {/* ⛔ ~~the Refresh row~~ moved ONTO THE HEADER (2026-09-10)
                                — see `TrayRefreshButton`. It sat inside this
                                `total > 0` branch, so the one moment it was missing was
                                an empty tray somebody was waiting on. */}
                            <div className="px-1 pb-1 text-[11px] text-muted-foreground">
                                Drag onto an appointment or a driver · eye to preview
                            </div>
                            <ScrollList>
                                {/**
                                  * ⭐ NEWEST SCANNED FIRST — Pete: *"show the documents in
                                  * order of scanned"*. The server already returns the
                                  * SharePoint listing newest-first and the unfiled tray by
                                  * ReceivedUtc DESC, so this renders in the order it is
                                  * given rather than re-sorting two differently-shaped
                                  * lists against each other.
                                  *
                                  * ⚠️ SCANS ABOVE DOCUMENTS, deliberately. A scan is the
                                  * thing that just came off the printer and is the reason
                                  * somebody is looking; an unfiled document has already
                                  * been dealt with once.
                                  */}
                                {scans.map(s => (
                                    <TrayCard
                                        key={s.id}
                                        name={s.name}
                                        sub={`${prettyBytes(s.bytes ?? 0)}${s.modifiedUtc
                                            ? ` · ${new Date(s.modifiedUtc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`
                                            : ''}`}
                                        icon={<CloudDownload className="h-3.5 w-3.5 text-sky-600" />}
                                        title="Drag it onto an appointment or a driver to file it."
                                        selected={ticked.has(scanKey(s.id))}
                                        onToggleSelected={() => toggle(scanKey(s.id))}
                                        dragItems={dragItemsFor(scanKey(s.id), { kind: 'scan', itemId: s.id, name: s.name })}
                                        onPreview={() => setViewing({
                                            target: { kind: 'scan', facilityCode, itemId: s.id },
                                            name: s.name,
                                        })}
                                        onDelete={() => setConfirmDelete(s)}
                                    />
                                ))}
                                {unfiled.map(d => (
                                    <TrayCard
                                        key={d.documentId}
                                        name={d.fileName}
                                        sub={`${prettyBytes(d.bytes)} · ${d.source.toLowerCase()}`}
                                        /* ⛔⚤ ~~A GLIMPSE, NOT A VIEWER~~ - the glimpse is gone.
                                           The reasoning is in
                                           `DocumentThumb`'s own header; the eye is the viewer now. */
                                        icon={<DocumentThumb fileName={d.fileName} />}
                                        title="Already stored, not on an appointment yet — drag it onto an appointment or a driver"
                                        selected={ticked.has(docKey(d.documentId))}
                                        onToggleSelected={() => toggle(docKey(d.documentId))}
                                        dragItems={dragItemsFor(docKey(d.documentId), { kind: 'document', documentId: d.documentId, name: d.fileName })}
                                        onPreview={() => setViewing({
                                            target: { kind: 'document', documentId: d.documentId },
                                            name: d.fileName,
                                        })}
                                        onPrint={isLabelFile(d.fileName)
                                            ? () => void printDocument(d)
                                            : undefined}
                                        /* ⛔ THE ONE OR THE OTHER, NEVER BOTH — see
                                           onPrintPaper's note on TrayCard. A label goes
                                           to the Zebra; everything else to the dialog. */
                                        /**
                                          * ⛔ NO PRINTER AND NO SIGN ON AN IMAGE (Pete,
                                          * 2026-09-10). A JPEG in the tray is not paperwork
                                          * yet — it becomes a PDF when it is attached, and
                                          * both actions belong to that PDF.
                                          *
                                          * ⚠️ SIGNING A LOOSE JPEG WOULD STAMP SOMETHING
                                          * ABOUT TO BE SUPERSEDED, and printing one would
                                          * print the thing we are about to convert. Eye and
                                          * delete are the only two that make sense here.
                                          */
                                        onPrintPaper={isLabelFile(d.fileName) || isImageFile(d.fileName)
                                            ? undefined
                                            : () => void printPaper(d)}
                                        /* ⚠️ NOT ON A LABEL, and not on a signed copy —
                                           stamping a stamp gives one document two claims
                                           about who received the appointment. */
                                        onSign={isLabelFile(d.fileName) || isImageFile(d.fileName)
                                            || d.signedFromDocumentId
                                            ? undefined
                                            : () => setSigning(d)}
                                        printTitle={
                                            /* ⛔ NAME THE STATE. `agentReady` is null while
                                               probing and false only once the probe has
                                               actually failed — saying "not installed"
                                               during the probe accuses a working machine. */
                                            zebra.agentReady === false
                                                ? 'Print to the label printer — the print agent is not '
                                                  + 'answering on this machine'
                                                : 'Print to the label printer'
                                        }
                                        onDelete={() => setConfirmVoid(d)}
                                        deleteTitle="Not paperwork — take it out of the tray"
                                    />
                                ))}
                            </ScrollList>
                            {truncated && (
                                <div className="px-1 pt-1 text-[11px] text-amber-700">
                                    Showing the newest only — there is a backlog in the scan folder.
                                </div>
                            )}
                        </>
                    )}
                    {/**
                      * ⭐ THE DROP TARGET, SAID OUT LOUD. A drop zone nobody knows about
                      * is not a feature — and the panel already accepts drags of its
                      * own, so "you can also drop files here" is not guessable.
                      * ⚠️ THE FILE INPUT IS NOT A FALLBACK, IT IS THE KEYBOARD PATH.
                      * Drag-and-drop cannot be done without a mouse.
                      */}
                    <label className="mt-1.5 flex cursor-pointer items-center justify-center gap-1.5
                                      rounded border border-dashed px-2 py-1.5 text-[11px]
                                      text-muted-foreground hover:border-primary/50 hover:text-foreground">
                        <Upload className="h-3 w-3" />
                        {upl.busy ? 'Uploading…' : 'Drop files here, or browse'}
                        <input
                            type="file"
                            multiple
                            className="hidden"
                            disabled={upl.busy}
                            /**
                              * ⛔ THE INPUT IS CLEARED **BEFORE** THE UPLOAD IS AWAITED, and
                              * that is now safe ONLY because the shared loop snapshots first
                              * (#288). ⚠️ It was not safe before: `value = ''` empties the
                              * same live FileList the pending call was still holding, so the
                              * browse button failed exactly as silently as the drop did.
                              *
                              * ⭐ Clearing it here rather than in a `finally` is deliberate —
                              * it lets the same file be picked twice in a row, which is a
                              * thing people do when they think the first go did not work.
                              */
                            onChange={(e) => {
                                const files = snapshotFiles(e.target.files);
                                e.target.value = '';
                                void upl.run(files);
                            }}
                        />
                    </label>
                </div>
            )}

            {/**
              * ⛔ ConfirmDialog, NEVER window.confirm. Pete: *"this is lazy coding and we
              * never do this."*
              * ⭐ AND THE WORDING LEADS WITH THE RECYCLE BIN. A scheduler clearing junk
              * needs to know this is reversible, or they will leave rubbish in the tray
              * rather than risk binning a bill of lading.
              */}
            {/**
              * ⭐ RENDERED HERE, DRAWN BESIDE THE COLUMN. It portals itself to the body
              * so the column's own scrolling cannot clip it — see DocumentPreview.
              */}
            <DocumentPreview
                anchor={trayRef.current}
                target={viewing?.target ?? null}
                name={viewing?.name ?? ''}
                getToken={getToken}
                onClose={() => setViewing(null)}
                onDownload={downloadWhatIsOpen}
            />

            {/**
              * ⭐ A SEPARATE DIALOG, NOT A SHARED ONE WITH TWO WORDINGS. What happens
              * to a scan and what happens to a document differ in a way the scheduler
              * needs told: one is recoverable from SharePoint for 93 days, the other
              * leaves a permanent record that it was here and was removed.
              */}
            <ConfirmDialog
                open={confirmVoid !== null}
                title="Take this out of the tray?"
                message={
                    `"${confirmVoid?.fileName ?? ''}" is already stored in Dock, so it is `
                    + 'not deleted — it is marked as removed and stops appearing here.\n\n'
                    + 'Use this for things that are not paperwork. If it IS paperwork, drag '
                    + 'it onto an appointment or a driver instead.'
                }
                confirmLabel="Take it out"
                destructive
                busy={voiding}
                onConfirm={() => void runVoid()}
                onCancel={() => setConfirmVoid(null)}
            />

            <ConfirmDialog
                open={confirmDelete !== null}
                title="Delete this file from SharePoint?"
                message={
                    `"${confirmDelete?.name ?? ''}" will be moved to the SharePoint recycle bin, `
                    + 'where it can be restored for 93 days.\n\n'
                    + 'Use this for things that are not paperwork at all. If it IS paperwork, '
                    + 'drag it onto an appointment or a driver instead.'
                }
                confirmLabel="Move to recycle bin"
                destructive
                busy={deleting}
                onConfirm={() => void runDelete()}
                onCancel={() => setConfirmDelete(null)}
            />

            {/**
              * ⭐ STAMP AS RECEIVED, FROM THE TRAY (Pete, 2026-09-10).
              *
              * ⚠️ NO CARRIER OR APPOINTMENT IS PASSED, and that is correct here
              * rather than an omission: a tray document is BY DEFINITION not on a
              * load yet, so there is no carrier whose last stamp placement could
              * be recalled. ⛔ ScanFolderPanel passes them because it signs AFTER
              * filing; this signs BEFORE. The dialog already treats both as
              * optional and falls back to its default placement.
              */}
            <SignDocumentDialog
                open={signing !== null}
                document={signing}
                facilityCode={facilityCode}
                carrierCode={null}
                appointmentNumber={null}
                getToken={getToken}
                onClose={() => setSigning(null)}
                onSigned={() => { setSigning(null); void load(); }}
            />
        </div>
    );
}

/**
 * ⛔ ~~`RefreshButton`~~ REMOVED 2026-09-10 — it is `TrayRefreshButton`, shared.
 *
 * ⚠️ IT WAS PRIVATE TO THIS FILE, which is exactly why the local tray never got
 * one and Pete had no way to re-read the folder after a cross-pile move. A
 * control both trays need does not live inside one of them.
 *
 * ⛔ AND IT USED THE `disabled` ATTRIBUTE. A disabled button receives no drag
 * events; this row is a drop surface, and that same mistake on the source toggle
 * is what made all three icons look broken earlier today.
 */

/**
 * A capped, scrolling list that SAYS when there is more below.
 *
 * ⭐ PETE ASKED FOR THIS EXPLICITLY: *"It obviously should have down arrow when more
 * than can fit on the screen."*
 *
 * ⛔ A NATIVE SCROLLBAR IS NOT ENOUGH HERE. This sits in a column that already scrolls,
 * inside a panel of panels; an inner scrollbar is thin, often overlaid, and easy to
 * miss — and a scheduler who cannot see that three more scans exist will go and ask
 * where they went.
 *
 * ⚠️ MEASURED, NOT GUESSED FROM A COUNT. "More than six items" would be wrong the
 * moment a filename wraps to two lines. This watches the real scroll height and hides
 * the hint the moment you reach the bottom.
 */
function ScrollList({ children }: { children: React.ReactNode }) {
    const ref = useRef<HTMLDivElement>(null);
    const [more, setMore] = useState(false);

    const measure = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        // 2px of slack: sub-pixel layout can leave scrollTop a hair short of the end.
        setMore(el.scrollHeight - el.clientHeight - el.scrollTop > 2);
    }, []);

    useEffect(() => {
        measure();
        const el = ref.current;
        if (!el) return;
        // ⚠️ ResizeObserver as well as scroll — the list changes height when the poll
        // brings in a new scan, and nothing scrolls at that moment.
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        for (const child of Array.from(el.children)) ro.observe(child);
        return () => ro.disconnect();
    }, [measure, children]);

    return (
        <div className="relative">
            <div
                ref={ref}
                onScroll={measure}
                /**
                 * ⛔ `overscroll-contain` — THE OTHER HALF OF THE DISAPPEARING HEADER.
                 *
                 * ⚠️ This list has its own ceiling and its own scrollbar, so it sits
                 * INSIDE the section's scroller. Without this, reaching the bottom of
                 * the list hands the remaining wheel movement to the parent — SCROLL
                 * CHAINING — and the section scrolls when the reader only meant to
                 * reach the last document. That is what "the title bar disappears"
                 * feels like from the other side of the screen.
                 *
                 * ⭐ The header is safe either way now, but a wheel event that moves a
                 * list the reader was not pointing at is its own bug.
                 */
                className="max-h-56 space-y-1 overflow-y-auto overscroll-contain pr-0.5"
            >
                {children}
            </div>
            {more && (
                <div
                    className="pointer-events-none absolute inset-x-0 bottom-0 flex items-end justify-center
                               bg-gradient-to-t from-background via-background/80 to-transparent pt-4 pb-0.5"
                >
                    <ChevronsDown className="h-3.5 w-3.5 animate-bounce text-muted-foreground" />
                </div>
            )}
        </div>
    );
}

/* ⛔ ~~TrayCard~~ MOVED to `PaperworkTrayCard.tsx` so `ScanFolderPanel` can
   use the same row — see the note there on why five features landed in one
   tray and not the other. */
